/**
 * useIdentityLink Hooks
 * API hooks for managing external identity links (e.g. Microsoft Entra ID).
 * Used by the Settings page's "Linked Accounts" section.
 */

import { createQueryHook, createMutationHook } from './factories';
import { log } from '../../utils/errors/logger';

export interface IdentityLinkInfo {
  provider_key: string;
  display_name: string;
  tenant_id?: string | null;
  linked_at?: string | null;
}

export interface AvailableLinkProvider {
  provider_key: string;
  display_name: string;
}

export interface IdentityLinkStatus {
  identities: Record<string, IdentityLinkInfo>;
  available_providers: AvailableLinkProvider[];
}

export interface IdentityLinkStartResponse {
  authorize_url: string;
}

const identityLinkQueryKey = ['identity-link', 'status'] as const;

/**
 * Get the user's identity-link status: which providers are linked,
 * which are still available to link.
 */
export const useIdentityLinkStatus = createQueryHook<IdentityLinkStatus, void>({
  queryKey: () => identityLinkQueryKey,
  queryFn: (apiClient) =>
    apiClient.getLinkStatusApiIdentityLinkStatusGet().then(
      (response) => response.data as unknown as IdentityLinkStatus
    ),
  logContext: 'GET /api/identity-link/status',
  staleTime: 0, // Status flips after redirect callback — always refetch on mount.
});

/**
 * Start the OAuth-style link flow for a provider. The backend returns an
 * ``authorize_url``; we top-level-redirect the browser there. After the
 * external IdP callback the backend redirects back to ``/settings?...``,
 * where the page picks up the result.
 */
export const useStartIdentityLink = createMutationHook<
  IdentityLinkStartResponse,
  string
>({
  mutationFn: (apiClient, providerKey) =>
    apiClient
      .startLinkApiIdentityLinkProviderKeyStartGet(providerKey)
      .then((response) => response.data as unknown as IdentityLinkStartResponse),
  logContext: 'GET /api/identity-link/:providerKey/start',
  logMethod: 'GET',
  onSuccess: (data, providerKey) => {
    log.user.action('identity_link_start', 'useIdentityLink', { providerKey });
    if (data?.authorize_url) {
      window.location.href = data.authorize_url;
    }
  },
});

/**
 * Unlink an external identity. The user remains logged into SmartDoc but
 * loses ACL-filtered access to that provider's content until they re-link.
 */
export const useUnlinkIdentity = createMutationHook<
  Record<string, unknown>,
  string
>({
  mutationFn: (apiClient, providerKey) =>
    apiClient
      .unlinkIdentityApiIdentityLinkProviderKeyDelete(providerKey)
      .then((response) => response.data as Record<string, unknown>),
  invalidateQueries: [identityLinkQueryKey],
  logContext: 'DELETE /api/identity-link/:providerKey',
  logMethod: 'DELETE',
  onSuccess: (_data, providerKey) => {
    log.user.action('identity_link_unlinked', 'useIdentityLink', { providerKey });
  },
});
